import React, { useState, useEffect } from 'react';
import { Row, Col, Typography, Select, App, Space } from 'antd';
import { FilterOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import { 
  getPerformanceSummary, 
  getPerformanceAreas, 
  getPerformanceCharts, 
  getPerformanceSales, 
  getPropertyAreasForRole 
} from '../../services/dashboardService';
import { 
  PerformanceSummary, 
  AreaPerformance, 
  PerformanceChartsData, 
  SalesPerformance 
} from '../../types/dashboard';

// Import Modular Components
import SummaryCards from './components/SummaryCards';
import TrendsTabs from './components/TrendsTabs';
import FunnelConversionCard from './components/FunnelConversionCard';
import SourceAnalysisCards from './components/SourceAnalysisCards';
import AreaPerformanceTable from './components/AreaPerformanceTable';
import SalesPerformanceTable from './components/SalesPerformanceTable';

const { Title, Text } = Typography;

const MONTH_NAMES = [
  'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
  'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'
];

export const PerformanceDashboard: React.FC = () => {
  const { message } = App.useApp();

  // Filters state
  const [selectedYear, setSelectedYear] = useState<number>(dayjs().year());
  const [selectedMonth, setSelectedMonth] = useState<number>(0); // 0 = Full Year Accumulative
  const [selectedAreaId, setSelectedAreaId] = useState<string>(''); // empty string = All Areas

  // Data states
  const [summaryData, setSummaryData] = useState<PerformanceSummary | null>(null);
  const [areasData, setAreasData] = useState<AreaPerformance[] | null>(null);
  const [chartsData, setChartsData] = useState<PerformanceChartsData | null>(null);
  const [salesData, setSalesData] = useState<SalesPerformance[] | null>(null);
  const [propertyAreas, setPropertyAreas] = useState<any[]>([]);
  
  // Area sorting states
  const [areaSortBy, setAreaSortBy] = useState<string>('name');
  const [areaSortOrder, setAreaSortOrder] = useState<string>('desc');

  // Sales pagination, search, and sorting states
  const [salesPage, setSalesPage] = useState<number>(1);
  const [salesLimit, setSalesLimit] = useState<number>(10);
  const [salesSearch, setSalesSearch] = useState<string>('');
  const [salesSortBy, setSalesSortBy] = useState<string>('name');
  const [salesSortOrder, setSalesSortOrder] = useState<string>('desc');
  const [salesTotal, setSalesTotal] = useState<number>(0);

  // Loading states
  const [loadingAreasFilter, setLoadingAreas] = useState<boolean>(true);
  const [loadingSummary, setLoadingSummary] = useState<boolean>(true);
  const [loadingAreas, setLoadingAreasTable] = useState<boolean>(true);
  const [loadingCharts, setLoadingCharts] = useState<boolean>(true);
  const [loadingSales, setLoadingSales] = useState<boolean>(true);

  // Fetch filters (property areas for the user role)
  useEffect(() => {
    const fetchAreas = async () => {
      try {
        setLoadingAreas(true);
        const res = await getPropertyAreasForRole();
        if (res?.status === 'success') {
          setPropertyAreas(res.data);
          // If there is only 1 area, select it as default
          if (res.data?.length === 1) {
            setSelectedAreaId(res.data[0].id);
          }
        }
      } catch (err) {
        console.error('Failed to load property areas:', err);
        message.error('Gagal memuat filter area properti.');
      } finally {
        setLoadingAreas(false);
      }
    };
    fetchAreas();
  }, [message]);

  // Fetch general performance dashboard sections (Summary & Charts) when global filters change
  useEffect(() => {
    const fetchGeneralDashboard = () => {
      const params: any = { periode: selectedYear };
      if (selectedMonth > 0) {
        params.month = selectedMonth;
      }
      if (selectedAreaId) {
        params.property_area_id = selectedAreaId;
      }

      // Reset loading states for general sections
      setLoadingSummary(true);
      setLoadingCharts(true);

      // Reset general data states
      setSummaryData(null);
      setChartsData(null);

      // Call 1: Summary (Paling ringan, instan)
      getPerformanceSummary(params)
        .then(res => {
          if (res?.status === 'success') {
            setSummaryData(res.data?.summary);
          } else {
            message.error(res?.message || 'Gagal memuat ringkasan KPI.');
          }
        })
        .catch(err => {
          console.error('Failed to fetch summary:', err);
          message.error('Gagal memuat ringkasan KPI.');
        })
        .finally(() => setLoadingSummary(false));

      // Call 2: Charts, Trends & Funnel (Berat)
      getPerformanceCharts(params)
        .then(res => {
          if (res?.status === 'success') {
            setChartsData(res.data);
          } else {
            message.error(res?.message || 'Gagal memuat visualisasi chart.');
          }
        })
        .catch(err => {
          console.error('Failed to fetch charts:', err);
          message.error('Gagal memuat visualisasi chart.');
        })
        .finally(() => setLoadingCharts(false));

      // Reset area sorting defaults when global filters change
      setAreaSortBy('name');
      setAreaSortOrder('desc');

      // Reset sales page, search, and sorting defaults when global filters change
      setSalesPage(1);
      setSalesSearch('');
      setSalesSortBy('name');
      setSalesSortOrder('desc');
    };

    fetchGeneralDashboard();
  }, [selectedYear, selectedMonth, selectedAreaId, message]);

  // Fetch area performance list (triggers on sorting or global filters change)
  useEffect(() => {
    const fetchAreaPerformance = () => {
      const params: any = { 
        periode: selectedYear,
        sort_by: areaSortBy,
        sort_order: areaSortOrder
      };
      if (selectedMonth > 0) {
        params.month = selectedMonth;
      }
      if (selectedAreaId) {
        params.property_area_id = selectedAreaId;
      }

      setLoadingAreasTable(true);
      setAreasData(null);

      getPerformanceAreas(params)
        .then(res => {
          if (res?.status === 'success') {
            setAreasData(res.data?.performance_by_area);
          } else {
            message.error(res?.message || 'Gagal memuat performa kawasan.');
          }
        })
        .catch(err => {
          console.error('Failed to fetch area performance:', err);
          message.error('Gagal memuat performa kawasan.');
        })
        .finally(() => setLoadingAreasTable(false));
    };

    fetchAreaPerformance();
  }, [selectedYear, selectedMonth, selectedAreaId, areaSortBy, areaSortOrder, message]);

  // Fetch sales performance leaderboard (triggers on pagination, search, sorting, or global filters change)
  useEffect(() => {
    const fetchSalesLeaderboard = () => {
      const params: any = { 
        periode: selectedYear,
        page: salesPage,
        limit: salesLimit,
        sort_by: salesSortBy,
        sort_order: salesSortOrder
      };
      if (selectedMonth > 0) {
        params.month = selectedMonth;
      }
      if (selectedAreaId) {
        params.property_area_id = selectedAreaId;
      }
      if (salesSearch) {
        params.search = salesSearch;
      }

      setLoadingSales(true);
      setSalesData(null);

      getPerformanceSales(params)
        .then(res => {
          if (res?.status === 'success') {
            setSalesData(res.data?.performance_by_sales);
            setSalesTotal(res.data?.pagination?.total || 0);
          } else {
            message.error(res?.message || 'Gagal memuat leaderboard sales.');
          }
        })
        .catch(err => {
          console.error('Failed to fetch sales performance:', err);
          message.error('Gagal memuat leaderboard sales.');
        })
        .finally(() => setLoadingSales(false));
    };

    fetchSalesLeaderboard();
  }, [selectedYear, selectedMonth, selectedAreaId, salesPage, salesLimit, salesSearch, salesSortBy, salesSortOrder, message]);

  const handleAreaTableChange = (pagination: any, filters: any, sorter: any) => {
    // Handle Sorting (sort_by & sort_order)
    const activeKey = sorter?.columnKey || sorter?.column?.key;
    if (sorter && activeKey && sorter.order) {
      let backendSortBy = 'name';
      if (activeKey === 'property_area_name') {
        backendSortBy = 'name';
      } else if (activeKey === 'leads_realization') {
        backendSortBy = 'leads';
      } else if (activeKey === 'unit_realization') {
        backendSortBy = 'unit';
      } else if (activeKey === 'omzet_realization') {
        backendSortBy = 'omzet';
      }
      setAreaSortBy(backendSortBy);
      setAreaSortOrder(sorter.order === 'ascend' ? 'asc' : 'desc');
    } else {
      // If sorting is cleared, reset to default (name, desc)
      setAreaSortBy('name');
      setAreaSortOrder('desc');
    }
  };

  const handleSalesTableChange = (pagination: any, filters: any, sorter: any) => {
    // 1. Handle Pagination (page & limit)
    if (pagination) {
      setSalesPage(pagination.current || 1);
      setSalesLimit(pagination.pageSize || 10);
    }

    // 2. Handle Sorting (sort_by & sort_order)
    const activeKey = sorter?.columnKey || sorter?.column?.key;
    if (sorter && activeKey && sorter.order) {
      let backendSortBy = 'name';
      if (activeKey === 'user_name') {
        backendSortBy = 'name';
      } else if (activeKey === 'detail_hot') {
        backendSortBy = 'hot';
      } else if (activeKey === 'detail_prospect') {
        backendSortBy = 'rsv';
      } else if (activeKey === 'detail_ok') {
        backendSortBy = 'ok';
      } else if (activeKey === 'omzet_realization') {
        backendSortBy = 'omzet';
      }
      setSalesSortBy(backendSortBy);
      setSalesSortOrder(sorter.order === 'ascend' ? 'asc' : 'desc');
    } else {
      // If sorting is cleared, reset to default (name, desc)
      setSalesSortBy('name');
      setSalesSortOrder('desc');
    }
  };

  const handleSalesSearch = (value: string) => {
    setSalesSearch(value);
    setSalesPage(1); // Always reset to page 1 when performing search
  };

  // Default funnel conversion structure to prevent crashes when empty
  const defaultFunnelConversion = {
    total_leads: 0,
    followed_up: 0,
    qualified: 0,
    reservation: 0,
    closing: 0,
    stages_percentage: {
      followed_up_rate: 0,
      qualification_rate: 0,
      reservation_rate: 0,
      closing_rate: 0
    }
  };

  return (
    <div style={{ 
      padding: '24px', 
      minHeight: '100vh', 
      background: 'radial-gradient(circle at 50% 0%, #f8fafc 0%, #e2e8f0 100%)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Dynamic Style Injection for Premium Interactive Animations */}
      <style>{`
        .glass-card-interactive {
          transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1) !important;
        }
        .glass-card-interactive:hover {
          transform: translateY(-5px);
          box-shadow: 0 16px 40px rgba(123, 170, 24, 0.08), 0 4px 12px rgba(0, 0, 0, 0.02) !important;
          border-color: rgba(123, 170, 24, 0.35) !important;
        }
        
        .glass-table-container {
          transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1) !important;
        }
        .glass-table-container:hover {
          box-shadow: 0 12px 30px rgba(0, 0, 0, 0.04) !important;
          border-color: rgba(255, 255, 255, 0.45) !important;
        }
        
        .funnel-stage-item {
          transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1) !important;
        }
        .funnel-stage-item:hover {
          transform: translateY(-2px) scale(1.02);
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.06) !important;
          filter: brightness(1.03);
        }
        
        .glass-table .ant-table-tbody > tr:hover > td {
          background: rgba(123, 170, 24, 0.04) !important;
          transition: background 0.2s ease;
        }
        
        .glass-table .ant-table-thead > tr > th {
          background: rgba(248, 250, 252, 0.65) !important;
          font-weight: 700;
        }

        /* Bordered glass-table styling for clean column boundaries */
        .glass-table .ant-table.ant-table-bordered > .ant-table-container {
          border: 1px solid rgba(203, 213, 225, 0.8) !important;
          border-radius: 8px !important;
          overflow: hidden;
        }
        .glass-table.ant-table-bordered .ant-table-cell,
        .glass-table .ant-table.ant-table-bordered .ant-table-cell {
          border-color: rgba(226, 232, 240, 0.85) !important;
        }
        .glass-table .ant-table.ant-table-bordered .ant-table-thead > tr > th {
          border-bottom: 1px solid rgba(203, 213, 225, 0.85) !important;
          border-inline-end: 1px solid rgba(203, 213, 225, 0.75) !important;
        }
        .glass-table .ant-table.ant-table-bordered .ant-table-tbody > tr > td {
          border-bottom: 1px solid rgba(226, 232, 240, 0.85) !important;
          border-inline-end: 1px solid rgba(226, 232, 240, 0.75) !important;
        }
        .glass-table .ant-table.ant-table-bordered .ant-table-cell-fix-left-last {
          border-inline-end: 1px solid rgba(203, 213, 225, 0.9) !important;
        }

        /* Make active sorter carets highly visible, vibrant, and larger */
        .glass-table .ant-table-column-sorter-up.active .anticon,
        .glass-table .ant-table-column-sorter-down.active .anticon {
          color: #1890ff !important;
          font-size: 13px !important;
          filter: drop-shadow(0 1px 3px rgba(24, 144, 255, 0.35));
          transform: scale(1.25);
          transition: all 0.2s ease;
        }

        /* Customize inactive caret arrow colors to be softer but readable */
        .glass-table .ant-table-column-sorter-inner {
          gap: 2px;
        }
        .glass-table .ant-table-column-sorter-up:not(.active) .anticon,
        .glass-table .ant-table-column-sorter-down:not(.active) .anticon {
          color: #94a3b8 !important;
          opacity: 0.55;
        }
      `}</style>

      {/* Decorative Floating Ambient Orbs (Hardware Accelerated Blur) */}
      <div style={{
        position: 'absolute',
        top: '-150px',
        left: '-150px',
        width: '500px',
        height: '500px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(123, 170, 24, 0.12) 0%, rgba(255, 255, 255, 0) 75%)',
        filter: 'blur(60px)',
        zIndex: 0,
        pointerEvents: 'none',
        willChange: 'transform',
      }} />
      <div style={{
        position: 'absolute',
        top: '30%',
        right: '-200px',
        width: '600px',
        height: '600px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(24, 144, 255, 0.08) 0%, rgba(255, 255, 255, 0) 75%)',
        filter: 'blur(80px)',
        zIndex: 0,
        pointerEvents: 'none',
        willChange: 'transform',
      }} />
      <div style={{
        position: 'absolute',
        bottom: '-150px',
        left: '10%',
        width: '450px',
        height: '450px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(250, 173, 20, 0.06) 0%, rgba(255, 255, 255, 0) 75%)',
        filter: 'blur(70px)',
        zIndex: 0,
        pointerEvents: 'none',
        willChange: 'transform',
      }} />

      {/* Main Content Area */}
      <div style={{ position: 'relative', zIndex: 1 }}>
        {/* Title Header */}
        <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <Title level={2} style={{ margin: 0, color: '#1e293b', fontWeight: 700 }}>
              Dashboard Performa Properti & Sales
            </Title>
            <Text type="secondary">
              Pantau realisasi omzet, unit terjual, kepatuhan SLA, dan corong konversi secara real-time.
            </Text>
          </div>

          {/* Global Filters */}
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
            <Space>
              <FilterOutlined style={{ color: '#64748b' }} />
              <Text type="secondary">Filter:</Text>
            </Space>

            {/* Area Filter */}
            <Select
              loading={loadingAreasFilter}
              value={selectedAreaId}
              onChange={(val) => setSelectedAreaId(val)}
              style={{ width: 180 }}
              placeholder="Pilih Kawasan"
            >
              {propertyAreas.length > 1 && (
                <Select.Option value="">Semua Kawasan</Select.Option>
              )}
              {propertyAreas.map((area) => (
                <Select.Option key={area.id} value={area.id}>
                  {area.name}
                </Select.Option>
              ))}
            </Select>

            {/* Month Filter */}
            <Select
              value={selectedMonth}
              onChange={(val) => setSelectedMonth(val)}
              style={{ width: 140 }}
            >
              <Select.Option value={0}>Setahun Penuh</Select.Option>
              {MONTH_NAMES.map((name, idx) => (
                <Select.Option key={idx + 1} value={idx + 1}>
                  {name}
                </Select.Option>
              ))}
            </Select>

            {/* Year Filter */}
            <Select
              value={selectedYear}
              onChange={(val) => setSelectedYear(val)}
              style={{ width: 100 }}
            >
              {[2024, 2025, 2026, 2027].map((year) => (
                <Select.Option key={year} value={year}>
                  {year}
                </Select.Option>
              ))}
            </Select>
          </div>
        </div>

        {/* Content Shell Rendering with Individual Loading Skeletons */}
        <>
          {/* Section 1: Glassmorphic KPI Cards */}
          <SummaryCards summary={summaryData} loading={loadingSummary} />

          {/* Section 2: Trends Chart & Funnel Conversion */}
          <Row gutter={[20, 20]} style={{ marginBottom: '24px' }}>
            {/* Column A: Tren MoM Charts */}
            <Col xs={24} lg={16}>
              <TrendsTabs monthlyTrends={chartsData?.monthly_trends || []} loading={loadingCharts} />
            </Col>

            {/* Column B: Funnel Conversion */}
            <Col xs={24} lg={8}>
              <FunnelConversionCard funnelConversion={chartsData?.funnel_conversion || defaultFunnelConversion} loading={loadingCharts} />
            </Col>
          </Row>

          {/* Section 2.5: Analisis Media Promosi (Sumber Leads) */}
          <SourceAnalysisCards 
            leadsBySource={chartsData?.leads_by_source || []} 
            sourceMonthlyTrends={chartsData?.source_monthly_trends || []} 
            leadsByInterestReason={chartsData?.leads_by_interest_reason || []}
            loading={loadingCharts}
          />

          {/* Section 3: Detailed Tables (Kawasan & Sales) */}
          <Row gutter={[20, 20]}>
            {/* Table 1: Performa Per Kawasan */}
            <Col xs={24}>
              <AreaPerformanceTable 
                performanceByArea={areasData || []} 
                loading={loadingAreas} 
                sortBy={areaSortBy}
                sortOrder={areaSortOrder}
                onTableChange={handleAreaTableChange}
              />
            </Col>

            {/* Table 2: Performa Per Sales */}
            <Col xs={24}>
              <SalesPerformanceTable 
                performanceBySales={salesData || []} 
                loading={loadingSales} 
                page={salesPage}
                limit={salesLimit}
                total={salesTotal}
                sortBy={salesSortBy}
                sortOrder={salesSortOrder}
                onTableChange={handleSalesTableChange}
                onSearch={handleSalesSearch}
              />
            </Col>
          </Row>
        </>
      </div>
    </div>
  );
};

export default PerformanceDashboard;
